import { Bedriftsdata, Kundetype } from "codegen";
import styled, { css } from "styled-components";

import { colors } from "@ntemarked/mittne-components";

import { Box, FieldError, Grid, Hint, Label, Select, StepHeading, StepWrapper } from "pages/Sikring/opprett/CreateSikringPage";
import { CustomerMeteringPoints, canProceedWithoutSikringsprodukt } from "pages/Sikring/opprett/CustomerMeteringPoints";
import { CustomerMeteringPointRow, MeteringPointCheck } from "pages/Sikring/opprett/customerMeteringPointList";
import {
	CONTRACT_LOCKED,
	MAALEPUNKT_LOCKED,
	SIGNING_LOCKED,
	STEP_NAMES,
	WizardState,
	WizardStep,
	canEditMaalepunkter,
	getAddedMaalepunktIds,
	getExcludedMaalepunktIds,
	getFlyt,
	getStepNumber,
	skipsSigning,
} from "pages/Sikring/opprett/wizardTypes";
import { FLYT_INFO, SCOPE_LABEL } from "pages/Sikring/sikringUtils";
import { SikringOmfangType } from "pages/Sikring/types";
import CompanySearch from "pages/Strombestilling/orders/components/CompanySearch";
import MeterSearch, { MaalerMedValidering } from "pages/Strombestilling/orders/components/MeterSearch";
import { spotpriceAreaOptions } from "utils/SpotpriceArea";

interface Step1Props {
	wiz: WizardState;
	update: <K extends keyof WizardState>(field: K, value: WizardState[K]) => void;
	/** Bedriftssøk - sikring selges bare til bedrift, så kunden identifiseres med organisasjonsnummer. */
	company?: Bedriftsdata;
	setCompany: (company: Bedriftsdata | undefined) => void;
	selectedMaalepunkt: MaalerMedValidering | null;
	setSelectedMaalepunkt: (maalepunkt: MaalerMedValidering | null) => void;
}

/**
 * Kunde valgt og omfang angitt. På kundenivå må minst ett målepunkt være valgt - ellers avvises ordren - og
 * målepunktene som er lagt til for hånd og er valgt må være slått opp, så prisområdet deres er sjekket.
 * customerMeteringPoints er undefined mens kundens målepunkt hentes eller hentingen feilet, og da er steget
 * også ugyldig. På målepunktnivå uten kontrakt må NTE allerede levere til målepunktet. På kundenivå uten kontrakt må
 * alle målepunktene ha sikringsproduktet, unntatt i dev og lokalt.
 */
export function getMissingFieldsForStep1(
	wiz: WizardState,
	company?: Bedriftsdata,
	customerMeteringPoints?: CustomerMeteringPointRow[],
	selectedMaalepunkt?: MaalerMedValidering | null
): string[] {
	if (!company?.orgnr) return ["Velg en kunde."];
	if (wiz.scope === SikringOmfangType.Maalepunkt) {
		if (!wiz.maalepunktId) return ["Velg et målepunkt."];
		if (meterNotSuppliedWithoutContract(wiz, selectedMaalepunkt)) return ["Velg et målepunkt NTE leverer strøm til."];
		return [];
	}
	if (customerMeteringPoints === undefined) return ["Kundens målepunkt er ikke hentet ennå."];
	if (customerMeteringPoints.length === 0) return ["Kunden har ingen målepunkt i valgt prisområde. Legg til målepunkt."];
	if (wiz.skipContract && !canProceedWithoutSikringsprodukt() && customerMeteringPoints.some((mp) => !mp.harSikringsprodukt))
		return ["Alle målepunktene må ha sikringsproduktet når ordren ikke har kontrakt."];
	if (customerMeteringPoints.some((mp) => mp.checked && mp.check === MeteringPointCheck.Loading)) return ["Henter målepunktene som er lagt til."];
	const failedCount = customerMeteringPoints.filter((mp) => mp.checked && mp.check === MeteringPointCheck.Failed).length;
	if (failedCount > 0) return [`Kunne ikke sjekke ${failedCount} målepunkt. Prøv igjen, eller fjern haken.`];
	return customerMeteringPoints.some((mp) => mp.selected) ? [] : ["Velg minst ett målepunkt."];
}

/**
 * Uten kontrakt opprettes det ingen leveranse, så sikringen må gjelde et målepunkt NTE allerede leverer til.
 * Ukjent leveransestatus (feltet mangler) stopper ikke brukeren.
 */
function meterNotSuppliedWithoutContract(wiz: WizardState, maalepunkt?: MaalerMedValidering | null): boolean {
	return wiz.skipContract && maalepunkt?.eksisterendeNTEanlegg === false;
}

export function Step1Scope({ wiz, update, company, setCompany, selectedMaalepunkt, setSelectedMaalepunkt }: Readonly<Step1Props>) {
	const orgnr = company?.orgnr ?? "";
	const customerFound = !!orgnr;

	// Klikk inne i det valgte kortet bobler hit, og update() ville ellers markert skjemaet som endret.
	function selectScope(scope: SikringOmfangType) {
		if (scope === SikringOmfangType.Maalepunkt && MAALEPUNKT_LOCKED) return;
		if (wiz.scope !== scope) update("scope", scope);
	}

	return (
		<StepWrapper $gap="14px" $maxWidth="820px">
			<StepHeading>Steg 1 – Kunde og omfang</StepHeading>
			<TopGrid>
				<div>
					<Label>Kunde</Label>
					<CompanySearch company={company} setCompany={setCompany} />
				</div>

				{customerFound && (
					<Box>
						<Label style={{ marginBottom: "4px" }}>Flyt</Label>
						<Hint style={{ margin: "0 0 12px" }}>Velg hvilke steg ordren skal hoppe over før sikringen sendes til UtilityCloud.</Hint>
						<CheckboxLabel>
							<input
								type="checkbox"
								checked={skipsSigning(wiz)}
								disabled={SIGNING_LOCKED || wiz.skipContract}
								onChange={(e) => update("skipSigning", e.target.checked)}
							/>
							Hopp over signering
						</CheckboxLabel>
						<CheckboxLabel>
							<input
								type="checkbox"
								checked={wiz.skipContract}
								disabled={CONTRACT_LOCKED}
								onChange={(e) => update("skipContract", e.target.checked)}
							/>
							Hopp over kontrakt
						</CheckboxLabel>
						<Hint>{FLYT_INFO[getFlyt(wiz)]}</Hint>
						{wiz.skipContract && (
							<Hint>
								Steg {getStepNumber(WizardStep.Kontrakt)} ({STEP_NAMES[WizardStep.Kontrakt]}) hoppes over.
							</Hint>
						)}
					</Box>
				)}
			</TopGrid>

			{customerFound && (
				<ScopeGroup role={MAALEPUNKT_LOCKED ? "group" : "radiogroup"} aria-labelledby="omfang-label">
					<Label as="span" id="omfang-label" style={{ marginBottom: 0 }}>
						Omfang
					</Label>
					<>
						<Card $selected={wiz.scope === SikringOmfangType.Kunde} onClick={() => selectScope(SikringOmfangType.Kunde)}>
							<CardOption>
								{!MAALEPUNKT_LOCKED && (
									<input
										type="radio"
										name="omfang"
										checked={wiz.scope === SikringOmfangType.Kunde}
										onChange={() => selectScope(SikringOmfangType.Kunde)}
									/>
								)}
								<strong>{SCOPE_LABEL[SikringOmfangType.Kunde]}</strong>
							</CardOption>
							<CardDescription $noRadio={MAALEPUNKT_LOCKED}>Volumet fordeles på kundens målepunkt som ligger i valgt prisområde.</CardDescription>
							{wiz.scope === SikringOmfangType.Kunde && (
								<CardBody $noRadio={MAALEPUNKT_LOCKED}>
									<Grid>
										<div>
											<Label>Prisområde</Label>
											<Select
												value={wiz.prisomraade}
												onChange={(e) => update("prisomraade", e.target.value as WizardState["prisomraade"])}
											>
												{spotpriceAreaOptions.map((p) => (
													<option key={p.value} value={p.value}>
														{p.label}
													</option>
												))}
											</Select>
										</div>
										<FullWidth>
											<Hint style={{ margin: 0 }}>
												Volumet fordeles etter faktisk forbruk på kundens målepunkt i dette prisområdet, etter hvert som de registreres.
											</Hint>
										</FullWidth>
										<FullWidth>
											<CustomerMeteringPoints
												orgnr={orgnr}
												prisomraade={wiz.prisomraade}
												addedMeteringPointIds={getAddedMaalepunktIds(wiz)}
												editable={canEditMaalepunkter(wiz)}
												excludedMeteringPointIds={getExcludedMaalepunktIds(wiz)}
												setAddedMeteringPointIds={(ids) => update("addedMaalepunktIds", ids)}
												setExcludedMeteringPointIds={(ids) => update("excludedMaalepunktIds", ids)}
											/>
										</FullWidth>
									</Grid>
								</CardBody>
							)}
						</Card>
						<Card
							$selected={wiz.scope === SikringOmfangType.Maalepunkt}
							$disabled={MAALEPUNKT_LOCKED}
							onClick={() => selectScope(SikringOmfangType.Maalepunkt)}
						>
							<CardOption>
								{!MAALEPUNKT_LOCKED && (
									<input
										type="radio"
										name="omfang"
										checked={wiz.scope === SikringOmfangType.Maalepunkt}
										onChange={() => selectScope(SikringOmfangType.Maalepunkt)}
									/>
								)}
								<strong>{SCOPE_LABEL[SikringOmfangType.Maalepunkt]}</strong>
							</CardOption>
							<CardDescription $noRadio={MAALEPUNKT_LOCKED}>
								{MAALEPUNKT_LOCKED ? "Ikke i salg." : "Sikringen gjelder bare valgt målepunkt."}
							</CardDescription>
							{wiz.scope === SikringOmfangType.Maalepunkt && (
								<CardBody>
									<Label>Velg målepunkt</Label>
									<MeterSearch
										kundetype={Kundetype.Bedrift}
										orgnummer={orgnr}
										selectedMeter={selectedMaalepunkt}
										setSelectedMeter={(mp) => {
											setSelectedMaalepunkt(mp);
											update("maalepunktId", mp?.maalepunktId ?? "");
										}}
									/>
									{meterNotSuppliedWithoutContract(wiz, selectedMaalepunkt) && (
										<FieldError>
											NTE leverer ikke strøm til dette målepunktet. Uten kontrakt kan sikringen bare gjelde målepunkt NTE allerede leverer
											til.
										</FieldError>
									)}
								</CardBody>
							)}
						</Card>
					</>
				</ScopeGroup>
			)}
		</StepWrapper>
	);
}

const Card = styled.div<{ $selected: boolean; $disabled?: boolean }>`
	padding: 16px 18px;
	border-radius: 9px;
	cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
	opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
	${(props) =>
		props.$selected
			? css`
					box-shadow: inset 0 0 0 2px ${colors.blue};
					background: ${colors.blue100};
				`
			: css`
					box-shadow: inset 0 0 0 1px ${colors.grey300};
				`}
`;

const TopGrid = styled.div`
	display: flex;
	flex-direction: column;
	gap: 18px;
`;

const CARD_INDENT = "30px";

const ScopeGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 14px;
`;

const FullWidth = styled.div`
	grid-column: 1 / -1;
`;

const CardBody = styled.div<{ $noRadio?: boolean }>`
	margin: 16px 0 0 ${(props) => (props.$noRadio ? 0 : CARD_INDENT)};
	cursor: auto;
`;

const CardOption = styled.label`
	display: flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;
`;

const CardDescription = styled.p<{ $noRadio?: boolean }>`
	margin: 8px 0 0 ${(props) => (props.$noRadio ? 0 : CARD_INDENT)};
	color: ${colors.grey800};
`;

const CheckboxLabel = styled.label`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 6px;
	cursor: pointer;
`;
